import axios, { AxiosResponse } from 'axios';
import Http from 'shared/http';
import semver from 'semver';
import {
  getLogiGroupIdsAndRoles,
  generateLogiToken,
  decryptText,
  getLogiVersion,
  generateLogiTokenForV6,
  generateLogiTokenForV23
} from 'utils/auth';
import {
 getUserGroups, getLogiUsers, addLogiUserToUserGroup, createLogiUser, updateLogiUser, getUserByUserId, isLogiUserMissingRoles, isLogiUserMissingGroups
} from 'utils/logi/logi-v23-utils';
import { dashboardPrivileges } from 'shared/constants';
import { ServerResponse } from 'data/types';
import AuthActions, {
  RenewAuthenticationTimeoutAction,
  RENEW_AUTHENTICATION_TIMEOUT,
  SetAuthenticationExpiredAction,
  SET_AUTHENTICATION_EXPIRED,
  StartAuthenticationTimeoutAction,
  START_AUTHENTICATION_TIMEOUT,
  UpdateSessionRejectedAction,
} from './authentication.types';

interface CreateLogiUserArgs {
  user: string;
  password: string;
  privileges: string[];
  externalDashboardIP: string;
  logiAdminAccount: string;
  logiAdminUserName: string;
  logiAdminPswd: string;
  logiSupervisorUserName: string;
  logiSupervisorPswd: string;
}

interface LogiAuthArgs extends CreateLogiUserArgs {
  logiOAuthClientId: string;
  logiOAuthClientName: string;
  logiOAuthClientSecret: string;
}

interface LogiTokenAuthArgs {
  user: string;
  externalDashboardIP: string;
  logiOAuthClientId: string;
  logiOAuthClientName: string;
  logiOAuthClientSecret: string;
  logiSupervisorUserName: string;
  logiSupervisorPswd: string;
}

interface LogiUserPrivilegesArgs {
  privileges: string[];
  externalDashboardIP: string;
  logiAdminAccount: string;
  logiAdminUserName: string;
  logiAdminPswd: string;
  logiSupervisorUserName: string;
  logiSupervisorPswd: string;
  logiUserId: string;
  user: string;
  password: string;
}

const http = Http.getHttp();

// action creators
export const doLogin = (body: any) => ({ type: AuthActions.LOGIN, payload: http.post('/users/authenticate', body) });
export const doFetchLoginMessage = () => ({ type: AuthActions.LOGINMESSAGE, payload: http.get('/prelogin/latest') });
export const clearLoginMessage = () => ({ type: AuthActions.LOGINMESSAGE_CLEAR });
const doLogout = () => ({ type: AuthActions.LOGOUT, payload: http.post('/logout') });
const doBeforeLogout = () => ({ type: AuthActions.BEFORE_LOGOUT });

/**
 * This function dispatches logout events and performs logout
 * after the event handler is completed
 */
export const handleLogout = () => {
  return async (dispatch: any) => {
    await dispatch(doBeforeLogout());
    // Let all the BEFORE_LOGOUT listeners complete their actions
    // (request + response) before calling doLogout. Calling doLogout
    // too early will cause REST API server unable to complete the outstanding
    // before_logout requests.
    setTimeout(() => {
      dispatch(doLogout());
    }, 1000);
  };
};

export const setPasswordUpdateFlag = () => ({ type: AuthActions.PASSWORD_UPDATE });
export const undoSetPasswordUpdateFlag = () => ({ type: AuthActions.UNDOPASSWORD_UPDATE });
export const doCheckToken = (token: string) =>
  ({ type: AuthActions.CHECK_TOKEN, payload: http.get(`/session?x-auth-token=${token}`) });
export const clearErrorStatus = () => ({ type: AuthActions.AUTH_CLEAR_ERRORSTATUS });
export const doExtendSession = () =>
  ({ type: AuthActions.EXTEND_SESSION, payload: http.get('/extend-session') });

export function doExtendSessionRejected(
  response: AxiosResponse<ServerResponse<any>>,
): UpdateSessionRejectedAction {
  return {
    type: AuthActions.EXTEND_SESSION_REJECTED,
    payload: response,
  };
}

export function startAuthenticationTimeout(
  authenticationTimeout: number,
): StartAuthenticationTimeoutAction {
  return {
    type: START_AUTHENTICATION_TIMEOUT,
    payload: { authenticationTimeout },
  };
}

export function renewAuthenticationTimeout(): RenewAuthenticationTimeoutAction {
  return {
    type: RENEW_AUTHENTICATION_TIMEOUT,
  };
}

export function setAuthenticationExpired(): SetAuthenticationExpiredAction {
  return {
    type: SET_AUTHENTICATION_EXPIRED,
  };
}

export const setCurrentPathname = (currentPathname: string) =>
  ({ type: AuthActions.SET_CURRENT_PATHNAME, payload: { currentPathname } });

// TODO: Refactor Logi login code - move to separate file?
export const checkLogiToken = ({
  user,
  externalDashboardIP,
  logiOAuthClientId,
  logiOAuthClientName,
  logiOAuthClientSecret,
  logiSupervisorUserName,
  logiSupervisorPswd,
}: LogiTokenAuthArgs) => {
  return async () => {
    if (localStorage.getItem('logi_auth_token')) {
      if (
        new Date().getTime() >
        new Date(
          localStorage.getItem('logi_auth_token_expiration') as string,
        ).getTime()
      ) {
        await generateLogiToken({
          user,
          externalDashboardIP,
          logiOAuthClientId,
          logiOAuthClientName,
          logiOAuthClientSecret,
          logiSupervisorUserName,
          logiSupervisorPswd,
        });
      }
    }
  };
};

export const createLogiUserForV6 = ({
  user,
  password,
  privileges,
  externalDashboardIP,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd,
}: CreateLogiUserArgs) => {
  return async () => {
    try {
      const {
        groupIds,
      } = await getLogiGroupIdsAndRoles({
        externalDashboardIP,
        logiAdminAccount,
        logiAdminPswd,
        logiAdminUserName,
        privileges,
      });

      await axios.post(
        `${externalDashboardIP}/composer/api/users`,
        {},
        {
          headers: {
            Accept: 'application/vnd.composer.v2+json',
            'Content-Type': 'application/vnd.composer.v2+json',
          },
          auth: {
            username: decryptText(logiAdminUserName),
            password: decryptText(logiAdminPswd),
          },
          data: {
            accountId: logiAdminAccount,
            accounts: [
              {
                accountDisabled: false,
                accountId: logiAdminAccount,
                accountName: 'company',
                groups: groupIds,
                disabled: false,
                userAttributes: [],
              },
            ],
            activeGroups: groupIds,
            availableAttributeKeys: [],
            deletable: false,
            external: false,
            forcePasswordChange: false,
            fullname: user,
            languageLocaleId: 'en_US',
            localeSettingsId: 'en_US',
            name: user,
            password,
            showQuickstart: false,
            version: 1,
          },
        },
      );

    } catch (err) {
      // TODO: log error
      // console.log('Error while creating user: ', err);
    }
  };
};

export const createLogiUserForV23 = ({
  user,
  password,
  privileges,
  externalDashboardIP,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd,
}: CreateLogiUserArgs) => {
  return async () => {
    try {
      const groupsMap = await getUserGroups(externalDashboardIP, logiAdminUserName, logiAdminPswd);
      console.log(`groupmap:${groupsMap}`);
      const logiUser = await createLogiUser(externalDashboardIP, logiAdminUserName, logiAdminPswd, logiAdminAccount, user);
      console.log(`created new logi user: ${logiUser}`);
      if (logiUser) {
        await addLogiUserToUserGroup(externalDashboardIP, logiAdminUserName, logiAdminPswd, logiAdminAccount, groupsMap['Regular Users'].id, 'Regular Users', logiUser.id);
        console.log(`added logi user to group: ${logiUser}`);
      }
    } catch (err) {
      console.log('Error while creating user in Logi: ', err);
    }
  };
};

const editLogiUserPrivilegesForV6 = ({
  privileges,
  externalDashboardIP,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd,
  logiUserId,
  user,
  password,
}: LogiUserPrivilegesArgs, logiVersion: string) => {
  return async () => {
    try {

      const acceptHeader = 'application/vnd.composer.v2+json';
      const {
        // roles,
        groupIds,
      } = await getLogiGroupIdsAndRoles({
        externalDashboardIP,
        logiAdminAccount,
        logiAdminPswd,
        logiAdminUserName,
        privileges,
      });

      const {
        data: { version },
      } = await axios.get(
        `${externalDashboardIP}/composer/api/users/${logiUserId}`,
        {
          headers: {
            Accept: acceptHeader,
          },
          auth: {
            username: decryptText(logiSupervisorUserName),
            password: decryptText(logiSupervisorPswd),
          },
        },
      );

      await axios.put(
        `${externalDashboardIP}/composer/api/users/${logiUserId}`,
        {},
        {
          headers: {
            Accept: acceptHeader,
            'Content-Type': acceptHeader,
          },
          auth: {
            username: decryptText(logiAdminUserName),
            password: decryptText(logiAdminPswd),
          },
          data: {
            accountId: logiAdminAccount,
            accounts: [
              {
                accountDisabled: false,
                accountId: logiAdminAccount,
                accountName: 'company',
                groups: groupIds,
                roles: [],
                disabled: false,
                userAttributes: [],
              },
            ],
            activeGroups: groupIds,
            availableAttributeKeys: [],
            deletable: false,
            external: false,
            forcePasswordChange: false,
            fullname: user,
            languageLocaleId: 'en_US',
            localeSettingsId: 'en_US',
            name: user,
            password,
            showQuickstart: false,
            version,
          },
        },
      );
    } catch (err) {
      // TODO: log error
      // console.log('Error while editing Logi user privileges: ', err);
    }
  };
};

const editLogiUserPrivilegesForV23 = ({
  privileges,
  externalDashboardIP,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd,
  logiUserId,
  user,
  password,
}: LogiUserPrivilegesArgs) => {
  return async () => {
    try {
      const groupsMap = await getUserGroups(externalDashboardIP, logiAdminUserName, logiAdminPswd);
      const logiUser = await getUserByUserId(externalDashboardIP, logiAdminUserName, logiAdminPswd, logiUserId);
      if (isLogiUserMissingRoles(logiUser)) {
        await updateLogiUser(externalDashboardIP, logiAdminUserName, logiAdminPswd, logiAdminAccount, logiUserId, user, [groupsMap['Regular Users'].id], password, logiUser.version);
      } else if (isLogiUserMissingGroups(logiUser, groupsMap['Regular Users'])) {
        await addLogiUserToUserGroup(externalDashboardIP, logiAdminUserName, logiAdminPswd, logiAdminAccount, groupsMap['Regular Users'].id, 'Regular Users', logiUser.id);
      }
    } catch (err) {
      console.log('Error while updating Logi user privileges: ', err);
    }
  };
};

export const authenticateLogiUser = ({
  user,
  password,
  privileges,
  externalDashboardIP,
  logiOAuthClientId,
  logiOAuthClientSecret,
  logiOAuthClientName,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd
}: LogiAuthArgs) => {
  return async (dispatch: any) => {
    console.log('authentication logi user');
    const version = await getLogiVersion(externalDashboardIP);
    if (semver.gte(version, '23.0.0')) {
      await authenticateLogiUserForV23({
        user,
        password,
        privileges,
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientSecret,
        logiOAuthClientName,
        logiAdminAccount,
        logiAdminUserName,
        logiAdminPswd,
        logiSupervisorUserName,
        logiSupervisorPswd
      }, dispatch);
      await dispatch({
        type: 'LOGI_TOKEN_UPDATE',
      });
    } else {
      await authenticateLogiUserForV6({
        user,
        password,
        privileges,
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientSecret,
        logiOAuthClientName,
        logiAdminAccount,
        logiAdminUserName,
        logiAdminPswd,
        logiSupervisorUserName,
        logiSupervisorPswd
      }, version, dispatch);
    }
  };
};

export const authenticateLogiUserForV6 = async (
  {
    user,
    password,
    privileges,
    externalDashboardIP,
    logiOAuthClientId,
    logiOAuthClientSecret,
    logiOAuthClientName,
    logiAdminAccount,
    logiAdminUserName,
    logiAdminPswd,
    logiSupervisorUserName,
    logiSupervisorPswd
  }: LogiAuthArgs,
  version: string,
  dispatch: (a: any) => void
) => {
  let logiUser: any;
  const headers = {
    headers: {
      Accept: 'application/vnd.composer.v2+json'
    },
    auth: {
      username: decryptText(logiSupervisorUserName),
      password: decryptText(logiSupervisorPswd)
    }
  };

  const hasDashboardPrivilege = privileges.some((r) => dashboardPrivileges.indexOf(r) >= 0);

  if (hasDashboardPrivilege) {
    const logiUsersUrl = `${externalDashboardIP}/composer/api/users?limit=-1&offset=0`;

    try {
      const { data: logiUsers } = await axios.get(logiUsersUrl, headers);

      const logiUserExists = logiUsers.some((currLogiUser: any) => {
        logiUser = currLogiUser;
        return currLogiUser.name === user;
      });

      const logiAdminUser = logiUsers.filter(
        (user: any) => user.name === 'admin'
      );

      if (!logiUserExists) {
        await dispatch(
          createLogiUserForV6({
            user,
            password,
            privileges,
            externalDashboardIP,
            logiAdminAccount: logiAdminUser[0]?.accountId,
            logiAdminUserName,
            logiAdminPswd,
            logiSupervisorUserName,
            logiSupervisorPswd
          })
        );
      } else if (logiUser) {
        await dispatch(
          editLogiUserPrivilegesForV6({
            privileges,
            externalDashboardIP,
            logiAdminAccount: logiAdminUser[0]?.accountId,
            logiAdminUserName,
            logiAdminPswd,
            logiSupervisorUserName,
            logiSupervisorPswd,
            logiUserId: logiUser.id,
            user,
            password
          }, version)
        );
      }

      interface ClientOAuth {
        id: string;
        clientId: string;
        clientSecret: string;
        grantTypes: [string];
        registeredRedirectUri: [string];
        clientName: string;
        accessTokenValiditySeconds: number;
        autoApprove: boolean;
      }

      const OAuthClientUrl = `${externalDashboardIP}/composer/api/oauth2/client`;
      const response = await axios.get(OAuthClientUrl, headers);
      const clientIdExists = response.data.some(
        (element: ClientOAuth) => element.clientName === 'dx_oauth'
      );

      if (clientIdExists) {
        logiOAuthClientId = response.data.filter(
          (element: ClientOAuth) => element.clientName === 'dx_oauth'
        )[0].clientId;
      } else {
        const OAuthClientBody = {
          accessTokenValiditySeconds: 600,
          autoApprove: true,
          clientName: 'dx_oauth',
          registeredRedirectUri: []
        };
        const postHeaders = {
          ...headers,
          headers: { 'Content-Type': 'application/vnd.composer.v2+json' }
        };
        const newResponse = await axios.post(
          OAuthClientUrl,
          OAuthClientBody,
          postHeaders
        );
        logiOAuthClientId = newResponse.data.clientId;
      }

      const { tokenValue, expiration } = await generateLogiTokenForV6({
        user,
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientName,
        logiOAuthClientSecret,
        logiSupervisorUserName,
        logiSupervisorPswd
      });

      localStorage.setItem('logi_auth_token', tokenValue);
      localStorage.setItem('logi_auth_token_expiration', expiration);

      await dispatch({
        type: 'LOGI_TOKEN_UPDATE'
      });
    } catch (err) {
      // TODO: Implement some sort of error log here
      // console.log('Error while authenticating Logi user: ', err);
    }
  }
};

export const authenticateLogiUserForV23 = async (
  {
    user,
    password,
    privileges,
    externalDashboardIP,
    logiOAuthClientId,
    logiOAuthClientSecret,
    logiOAuthClientName,
    logiAdminAccount,
    logiAdminUserName,
    logiAdminPswd,
    logiSupervisorUserName,
    logiSupervisorPswd
  }: LogiAuthArgs,
  dispatch: (a: any) => void
) => {
    const hasDashboardPrivilege = privileges.some((r) => dashboardPrivileges.indexOf(r) >= 0);

    if (hasDashboardPrivilege) {
      try {
        // for Logi v23+ , admin user will have the supervisor permission. Logi wont have default supervisor user
        const logiUsers = await getLogiUsers(externalDashboardIP, logiAdminUserName, logiAdminPswd);
        const currLogiUser = logiUsers.find((c) => c.name === user);
        console.log(`currLogiUser: ${currLogiUser}`);
        if (!currLogiUser) {
          await dispatch(
            createLogiUserForV23({
              user,
              password,
              privileges,
              externalDashboardIP,
              logiAdminAccount,
              logiAdminUserName,
              logiAdminPswd,
              logiSupervisorUserName,
              logiSupervisorPswd,
            })
          );
        } else {
          await dispatch(
            editLogiUserPrivilegesForV23({
              privileges,
              externalDashboardIP,
              logiAdminAccount,
              logiAdminUserName,
              logiAdminPswd,
              logiSupervisorUserName,
              logiSupervisorPswd,
              logiUserId: currLogiUser.id,
              user,
              password
            })
          );
        }
        const { tokenValue, expiration } = await generateLogiTokenForV23({
          user,
          externalDashboardIP,
          logiOAuthClientId,
          logiOAuthClientName,
          logiOAuthClientSecret,
          logiSupervisorUserName,
          logiSupervisorPswd
        });

        localStorage.setItem('logi_auth_token', tokenValue);
        localStorage.setItem('logi_auth_token_expiration', expiration);

        await dispatch({
          type: 'LOGI_TOKEN_UPDATE'
        });
      } catch (err) {
        // TODO: Implement some sort of error log here
        // console.log('Error while authenticating Logi user: ', err);
      }
    }
};
